import type { EventId, PlanEvent } from "../../../shared/types";
import { addDays } from "../../../shared/types/date-helpers";

function getDayOfWeek(dateStr: string): number {
  return new Date(dateStr + "T00:00:00").getDay();
}

function getStartOfWeek(dateStr: string): string {
  const dow = getDayOfWeek(dateStr);
  return addDays(dateStr, -dow);
}

function expandWeeklyWithDays(
  event: PlanEvent,
  startDate: string,
  endDate: string,
  instances: PlanEvent[],
) {
  const rule = event.recurrence!;
  const days = rule.daysOfWeek!;
  let weekStart = getStartOfWeek(event.date);

  while (weekStart <= endDate) {
    for (const dow of days) {
      const current = addDays(weekStart, dow);
      if (current < event.date) continue;
      if (current > endDate) break;
      if (rule.endDate && current > rule.endDate) break;
      if (current >= startDate && !event.excludedDates?.includes(current)) {
        const instanceId =
          current === event.date
            ? event.id
            : (`${event.id}_${current}` as EventId);
        instances.push({ ...event, id: instanceId, date: current });
      }
    }
    if (rule.endDate && weekStart > rule.endDate) break;
    weekStart = addDays(weekStart, 7 * rule.interval);
  }
}

export function expandRecurringEvents(
  events: PlanEvent[],
  startDate: string,
  endDate: string,
): PlanEvent[] {
  const instances: PlanEvent[] = [];
  for (const event of events) {
    if (!event.recurrence) {
      if (event.date >= startDate && event.date <= endDate) {
        instances.push(event);
      }
      continue;
    }
    const rule = event.recurrence;

    if (rule.type === "weekly" && rule.daysOfWeek && rule.daysOfWeek.length > 0) {
      expandWeeklyWithDays(event, startDate, endDate, instances);
      continue;
    }

    let current = event.date;
    while (current <= endDate) {
      if (rule.endDate && current > rule.endDate) break;
      if (current >= startDate && !event.excludedDates?.includes(current)) {
        const instanceId =
          current === event.date
            ? event.id
            : (`${event.id}_${current}` as EventId);
        instances.push({ ...event, id: instanceId, date: current });
      }
      switch (rule.type) {
        case "daily":
          current = addDays(current, rule.interval);
          break;
        case "weekly":
          current = addDays(current, 7 * rule.interval);
          break;
        case "monthly":
        case "quarterly":
        case "semiannual":
        case "yearly": {
          const d = new Date(current + "T00:00:00");
          const monthsToAdd = rule.type === "quarterly" ? 3
            : rule.type === "semiannual" ? 6
            : rule.type === "yearly" ? 12
            : rule.interval;
          d.setMonth(d.getMonth() + monthsToAdd);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          current = `${y}-${m}-${day}`;
          break;
        }
      }
    }
  }
  return instances.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.startTime.localeCompare(b.startTime);
  });
}
